'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Mail, Lock, ChevronRight, AlertCircle, Zap, ShieldCheck, Sparkles, Store, Shield } from 'lucide-react';
import { useAuth } from '@client/contexts/AuthContext';
import { useLanguage } from '@client/contexts/LanguageContext';

const DEMO_MODE_ON = process.env.NEXT_PUBLIC_DEMO_MODE === 'on';

interface FormErrors {
  email?: string;
  password?: string;
  general?: string;
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <p className="flex items-center gap-1 text-xs mt-1.5 font-medium text-red-500">
      <AlertCircle size={12} />
      {message}
    </p>
  );
}

export default function LoginPage() {
  const router = useRouter();
  const { signIn } = useAuth();
  const { t } = useLanguage();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const err = params.get('error');
      if (err === 'restaurant_suspended' || err === 'suspended') {
        setErrors(prev => ({
          ...prev,
          general: 'This restaurant account is currently suspended. Please contact platform administration.',
        }));
      }
    }
  }, []);

  const validateEmail = (val: string): string | undefined => {
    if (!val.trim()) return t('auth.emailRequired', 'Email address is required');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) return t('auth.emailInvalid', 'Please enter a valid email address');
  };

  const validatePassword = (val: string): string | undefined => {
    if (!val) return t('auth.passwordRequired', 'Password is required');
    if (val.length < 6) return t('auth.passwordMinLength', 'Password must be at least 6 characters');
  };

  const handleBlur = (field: string) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    if (field === 'email') setErrors(prev => ({ ...prev, email: validateEmail(email) }));
    if (field === 'password') setErrors(prev => ({ ...prev, password: validatePassword(password) }));
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {
      email: validateEmail(email),
      password: validatePassword(password),
    };
    setErrors(newErrors);
    setTouched({ email: true, password: true });
    return !newErrors.email && !newErrors.password;
  };

  const getRedirectPath = (role?: string | null) =>
    role === 'superadmin' || role === 'support' ? '/admin/dashboard' : '/restaurant-dashboard';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setErrors({});
    try {
      const { user } = await signIn(email, password);
      router.push(getRedirectPath(user?.role));
    } catch (err: any) {
      setErrors({ general: err?.message || t('auth.invalidCredentials', 'Invalid email or password. Please try again.') });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="relative min-h-[calc(100vh-140px)] flex items-center justify-center py-12 sm:py-20 px-4 sm:px-6">
      {/* Background Glow */}
      <div
        className="absolute inset-x-0 top-0 -z-10 h-[550px] opacity-40 pointer-events-none"
        aria-hidden
        style={{
          backgroundImage:
            'radial-gradient(ellipse 70% 60% at 50% 0%, var(--marketing-accent-soft, rgba(91,91,214,0.22)), transparent 75%)',
        }}
      />

      <div className="w-full max-w-md mx-auto">
        {/* Header Badge & Title */}
        <div className="text-center mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900 dark:text-white">
            {t('auth.welcomeBack', 'Welcome back')}
          </h1>
          <p className="text-xs sm:text-sm text-zinc-500 dark:text-zinc-400 mt-1.5">
            {t('auth.signInToDashboard', 'Sign in to access your orders, bookings & AI receptionists')}
          </p>
        </div>

        {/* Auth Card */}
        <div className="rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/90 shadow-xl backdrop-blur-xl p-6 sm:p-8">
          {errors.general && (
            <div className="flex items-center gap-2 p-3 rounded-xl mb-5 text-xs sm:text-sm bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/20">
              <AlertCircle size={15} className="shrink-0" />
              <span>{errors.general}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div>
              <label className="block text-xs font-semibold mb-1.5 text-zinc-700 dark:text-zinc-300">
                {t('auth.emailAddress', 'Email address')}
              </label>
              <div className="relative">
                <Mail
                  size={15}
                  className={`absolute left-3 top-1/2 -translate-y-1/2 transition-colors ${touched.email && errors.email ? 'text-red-500' : 'text-zinc-400'
                    }`}
                />
                <input
                  type="email"
                  className={`input-base text-sm pl-9 pr-3 py-2.5 w-full rounded-xl transition-all ${touched.email && errors.email ? 'border-red-500 focus:ring-red-500/20' : ''
                    }`}
                  placeholder={t('auth.emailPlaceholder', 'you@restaurant.com')}
                  value={email}
                  onChange={e => {
                    setEmail(e.target.value);
                    if (touched.email) setErrors(prev => ({ ...prev, email: validateEmail(e.target.value) }));
                  }}
                  onBlur={() => handleBlur('email')}
                />
              </div>
              <FieldError message={touched.email ? errors.email : undefined} />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-semibold text-zinc-700 dark:text-zinc-300">
                  {t('auth.password', 'Password')}
                </label>
                <Link
                  href="/forgot-password"
                  className="text-xs font-medium text-marketing-accent hover:underline"
                >
                  {t('auth.forgotPassword', 'Forgot password?')}
                </Link>
              </div>
              <div className="relative">
                <Lock
                  size={15}
                  className={`absolute left-3 top-1/2 -translate-y-1/2 transition-colors ${touched.password && errors.password ? 'text-red-500' : 'text-zinc-400'
                    }`}
                />
                <input
                  type={showPassword ? 'text' : 'password'}
                  className={`input-base text-sm pl-9 pr-10 py-2.5 w-full rounded-xl transition-all ${touched.password && errors.password ? 'border-red-500 focus:ring-red-500/20' : ''
                    }`}
                  placeholder="••••••••"
                  value={password}
                  onChange={e => {
                    setPassword(e.target.value);
                    if (touched.password) setErrors(prev => ({ ...prev, password: validatePassword(e.target.value) }));
                  }}
                  onBlur={() => handleBlur('password')}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              <FieldError message={touched.password ? errors.password : undefined} />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl text-sm font-semibold text-white bg-marketing-accent hover:bg-marketing-accent-hover transition-all duration-150 shadow-md shadow-marketing-accent/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-marketing-accent disabled:opacity-50 cursor-pointer mt-2"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>{t('auth.signingIn', 'Signing in…')}</span>
                </span>
              ) : (
                <span className="flex items-center gap-1.5">
                  <span>{t('auth.signIn', 'Sign In')}</span>
                  <ChevronRight size={15} />
                </span>
              )}
            </button>
          </form>

          {/* Quick Demo Access Pills */}
          <p className="text-center text-xs text-zinc-500 dark:text-zinc-400 mt-6">
            {t('auth.noAccount', "Don't have an account?")}{' '}
            <Link
              href="/register"
              className="font-semibold text-marketing-accent hover:underline"
            >
              {t('auth.createFree', 'Start 14-day free trial')}
            </Link>
          </p>
        </div>

        {/* Security / Trust Footer */}
        <div className="mt-6 flex items-center justify-center gap-4 text-[11px] text-zinc-400 dark:text-zinc-500">
          <span className="flex items-center gap-1">
            <ShieldCheck size={12} className="text-emerald-500" />
            <span>256-Bit SSL Encrypted</span>
          </span>
          <span>•</span>
          <span>99.9% Uptime SLA</span>
          <span>•</span>
          <span>SOC2 Ready</span>
        </div>
      </div>
    </section>
  );
}
